import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Pause, Play, Trash2, Zap } from 'lucide-react';
import { klarysUrl } from './klarysApi';
import { EnvoisProgrammes } from './KlarysEnvoisProgrammes';

const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const Tache = ({ t, headers, onReload }) => {
  const [busy, setBusy] = useState(false);
  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok(r)); onReload(); } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3 text-sm" data-testid={`klarys-tache-${t.id}`}>
      <div className="flex items-start gap-2">
        <CalendarClock className={`mt-0.5 h-4 w-4 ${t.actif ? 'text-emerald-600' : 'text-slate-400'}`} />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">{t.titre}</p>
          <p className="text-xs text-slate-600">{t.type_label} · {t.recurrence}{t.params?.produit ? ` · ${t.params.produit.toUpperCase()}` : ''}</p>
          <p className="mt-1 text-[11px] text-slate-500" data-testid={`klarys-tache-infos-${t.id}`}>
            {t.actif ? `Prochaine : ${fmt(t.prochaine_execution)}` : 'Suspendue'} · Dernière : {fmt(t.derniere_execution)}{t.dernier_resultat ? ` — ${t.dernier_resultat}` : ''} · {t.executions || 0} exécution(s)
          </p>
        </div>
        <div className="flex gap-1">
          <button type="button" disabled={busy} title="Exécuter maintenant" data-testid={`klarys-tache-run-${t.id}`}
            onClick={() => window.confirm(`Exécuter « ${t.titre} » maintenant ?`) && act(() => axios.post(klarysUrl(`/taches/${t.id}/executer`), {}, { headers }), (r) => r.data.resume)}
            className="rounded-md border border-slate-200 p-1.5 text-amber-600 hover:bg-amber-50"><Zap className="h-3.5 w-3.5" /></button>
          <button type="button" disabled={busy} title={t.actif ? 'Suspendre' : 'Réactiver'} data-testid={`klarys-tache-toggle-${t.id}`}
            onClick={() => act(() => axios.patch(klarysUrl(`/taches/${t.id}`), { actif: !t.actif }, { headers }), () => (t.actif ? 'Tâche suspendue' : 'Tâche réactivée'))}
            className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50">{t.actif ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}</button>
          <button type="button" disabled={busy} title="Supprimer" data-testid={`klarys-tache-delete-${t.id}`}
            onClick={() => window.confirm(`Supprimer la tâche « ${t.titre} » ?`) && act(() => axios.delete(klarysUrl(`/taches/${t.id}`), { headers }), () => 'Tâche supprimée')}
            className="rounded-md border border-slate-200 p-1.5 text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </li>
  );
};

export const KlarysTaches = ({ headers, superAdmin }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => axios.get(klarysUrl('/taches'), { headers }).then((r) => setItems(r.data.items)).catch(() => setItems([])), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (superAdmin) load(); }, [load, superAdmin]);
  return (
    <div className="h-full overflow-y-auto bg-[#f6f8fb] p-4" data-testid="klarys-taches">
      <EnvoisProgrammes headers={headers} />
      {superAdmin && (<>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Tâches récurrentes</h3>
      <p className="mb-3 text-xs text-slate-600">
        Demandez à Klarys, par exemple : « relance les impayés DOS-DOMIUM chaque lundi à 9 h ». Après votre confirmation, la tâche s'exécute automatiquement
        et chaque exécution est inscrite au journal. Types planifiables : relances d'impayés, rapport de synthèse, export comptable, relevé des impayés, nettoyage des devis à 0 €.
      </p>
      {!items ? <div className="text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /></div>
        : !items.length ? <p className="text-sm text-slate-500" data-testid="klarys-taches-empty">Aucune tâche planifiée.</p>
          : <ul className="space-y-2">{items.map((t) => <Tache key={t.id} t={t} headers={headers} onReload={load} />)}</ul>}
      </>)}
    </div>
  );
};
